import React, { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, StickyNote, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";

interface PlayerNote {
  id: string;
  title: string;
  content: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface PlayerNotesPanelProps {
  adventureId?: string;
}

export function PlayerNotesPanel({ adventureId }: PlayerNotesPanelProps) {
  const { t } = useLocale();
  const [notes, setNotes] = useState<PlayerNote[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  // userId state drives the realtime subscription filter so we only receive
  // our own note changes (not every player's in the adventure).
  const [userId, setUserId] = useState<string | null>(null);
  // Ref mirrors state so fetchNotes (a useCallback) can read it without re-binding.
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    getSessionUser().then(user => {
      userIdRef.current = user?.id ?? null;
      setUserId(user?.id ?? null);
    });
  }, []);

  const fetchNotes = useCallback(async () => {
    if (!adventureId) return;
    // Always retrieve userId fresh if not yet cached (first call).
    if (!userIdRef.current) {
      const user = await getSessionUser();
      userIdRef.current = user?.id ?? null;
    }
    if (!userIdRef.current) return;
    const { data } = await supabase
      .from("player_notes")
      .select("*")
      .eq("adventure_id", adventureId)
      .eq("user_id", userIdRef.current)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (data) setNotes(data as PlayerNote[]);
  }, [adventureId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  // Realtime — scoped to this user's notes only; wait until userId is known.
  useEffect(() => {
    if (!adventureId || !userId) return;
    const channel = supabase
      .channel(`player-notes-${adventureId}-${userId}`)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "player_notes",
        // Double-filter: only receive events for this adventure AND this user.
        // Prevents every player's note change from triggering a re-fetch on
        // every other player's client (audit F-14).
        filter: `adventure_id=eq.${adventureId}`,
      }, (payload) => {
        // Secondary guard: filter to own notes in case Supabase realtime
        // doesn't support compound filters in this version.
        if ((payload.new as any)?.user_id && (payload.new as any).user_id !== userId) return;
        fetchNotes();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [adventureId, userId, fetchNotes]);

  const startCreate = () => {
    setCreating(true);
    setEditTitle("");
    setEditContent("");
    setEditingId(null);
  };

  const startEdit = (note: PlayerNote, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(note.id);
    setEditTitle(note.title);
    setEditContent(note.content);
    setCreating(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setCreating(false);
    setEditTitle("");
    setEditContent("");
  };

  const saveNote = async () => {
    if (!adventureId || (!editTitle.trim() && !editContent.trim())) return;

    const user = await getSessionUser();
    if (!user) return;

    if (creating) {
      await supabase.from("player_notes").insert({
        adventure_id: adventureId,
        user_id: user.id,
        title: editTitle.trim() || t.notes_untitled_note,
        content: editContent.trim(),
        sort_order: notes.length,
      } as any);

      // Index for RAG
      indexNoteForRAG(adventureId, editTitle.trim() || t.notes_untitled_note, editContent.trim());
    } else if (editingId) {
      await supabase.from("player_notes").update({
        title: editTitle.trim() || t.notes_untitled_note,
        content: editContent.trim(),
        updated_at: new Date().toISOString(),
      } as any).eq("id", editingId);

      // Re-index for RAG
      indexNoteForRAG(adventureId, editTitle.trim() || t.notes_untitled_note, editContent.trim(), editingId);
    }

    cancelEdit();
    fetchNotes();
  };

  const deleteNote = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await supabase.from("player_notes").delete().eq("id", id);
    // Remove from RAG index
    await supabase.from("document_chunks").delete().eq("source_type", "player_note").eq("source_id", id);
    fetchNotes();
  };

  if (!adventureId) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <StickyNote className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Player Notes</span>
        <Badge variant="secondary" className="ml-auto text-[10px]">{notes.length}</Badge>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={startCreate} aria-label="New note">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Create/Edit form */}
      {(creating || editingId) && (
        <div className="rounded-md border border-primary/30 bg-accent/20 p-2 space-y-2">
          <Input
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            placeholder={t.notes_title_placeholder}
            className="h-7 text-xs"
            autoFocus
          />
          <Textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            placeholder={t.notes_content_placeholder}
            className="text-xs min-h-[60px] resize-none"
            rows={3}
          />
          <div className="flex justify-end gap-1.5">
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={cancelEdit}>
              <X className="h-3 w-3 mr-1" /> Cancel
            </Button>
            <Button size="sm" className="h-6 text-xs" onClick={saveNote}>
              <Save className="h-3 w-3 mr-1" /> Save
            </Button>
          </div>
        </div>
      )}

      {notes.length === 0 && !creating ? (
        <p className="text-xs text-muted-foreground/70 italic text-center py-3">
          No notes yet. Add notes to track your plans, theories, or observations.
        </p>
      ) : (
        <ScrollArea className="max-h-[300px]">
          <div className="space-y-1">
            {notes.map(note => (
              <button
                key={note.id}
                onClick={() => setExpanded(expanded === note.id ? null : note.id)}
                className={cn(
                  "w-full text-left rounded-md border border-border/50 p-2 transition-colors hover:bg-accent/40",
                  expanded === note.id && "bg-accent/30 border-primary/30",
                  editingId === note.id && "hidden"
                )}
              >
                <div className="flex items-center gap-2">
                  <StickyNote className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="text-xs font-medium text-foreground truncate flex-1">
                    {note.title || t.notes_untitled}
                  </span>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={(e) => startEdit(note, e)}
                      className="p-0.5 rounded hover:bg-accent transition-colors"
                    >
                      <Pencil className="h-2.5 w-2.5 text-muted-foreground" />
                    </button>
                    <button
                      onClick={(e) => deleteNote(note.id, e)}
                      className="p-0.5 rounded hover:bg-destructive/20 transition-colors"
                    >
                      <Trash2 className="h-2.5 w-2.5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                {expanded === note.id && note.content && (
                  <div className="mt-2 pt-2 border-t border-border/30">
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {note.content}
                    </p>
                  </div>
                )}
              </button>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

/** Index a player note into document_chunks for RAG retrieval */
async function indexNoteForRAG(adventureId: string, title: string, content: string, noteId?: string) {
  try {
    const { data: adventure } = await supabase
      .from("adventures")
      .select("world_id")
      .eq("id", adventureId)
      .single();
    if (!adventure) return;

    const chunkContent = `[Player Note] ${title}\n${content}`;
    const sourceId = noteId || `note_${Date.now()}`;

    // Remove old chunks for this note
    if (noteId) {
      await supabase.from("document_chunks").delete().eq("source_type", "player_note").eq("source_id", noteId);
    }

    // Insert new chunk
    await supabase.from("document_chunks").insert({
      world_id: adventure.world_id,
      content: chunkContent,
      source_type: "player_note",
      source_id: sourceId,
      chunk_index: 0,
      metadata: { adventure_id: adventureId, title, type: "player_note" },
    });
  } catch (e) {
    console.warn("[PLAYER_NOTES] RAG indexing failed:", e);
  }
}
